import { joinArraysWithConjunction } from '@/lang/formats.ts';
import { PositionCircleStates } from '@/types/map-config.ts';
import { BackgroundGeolocationPlugin } from "@capacitor-community/background-geolocation";
import { registerPlugin } from '@capacitor/core';
import { Geolocation } from '@capacitor/geolocation';
import { LocalNotifications } from '@capacitor/local-notifications';
import booleanIntersects from '@turf/boolean-intersects';
import circle from '@turf/circle';
import { featureCollection } from '@turf/helpers';
import { defineStore } from "pinia";
import { PolygonTypes } from "@movinglayers/map";
import { Monitoring } from '@movinglayers/monitoring/frontend';
import { computed, ref, watch } from "vue";
import { useI18n } from 'vue-i18n';
import { useActivityStore } from './activity.ts';
import { useAlertsStore } from './alerts.ts';
import { useFenceStore } from "./fence.ts";

const BackgroundGeolocation = registerPlugin<BackgroundGeolocationPlugin>("BackgroundGeolocation");
const waitTimeNotification = 5000; // the ionic docs say 1 seconds, making it 5 seconds to be sure

export const useNotificationsStore = defineStore("notification", () => {

    const currentWatchId = ref<string | null>(null);
    const currentPosition = ref<[number, number, number] | null>(null); // longitude, latitude and accuracy
    const notificationIdentifier = ref(1);

    const insideFenceDict = ref<{ [key: string]: boolean; }>({});
    const inaccuracyCounter = ref(0);

    const fenceStore = useFenceStore();
    const alertStore = useAlertsStore();
    const activityStore = useActivityStore();
    const { t, locale } = useI18n();

    const title = t("notification.insideFence");

    const createPositionCircle = (longitude: number, latitude: number, radius: number) => {
        return circle([longitude, latitude], radius, { units: "meters" });
    };

    const currentPositionCircleFeatureCollection = computed(() => {
        if (currentPosition.value && activityStore.isActivityRunning) {
            let state;
            if (isInsideFence.value) {
                state = PositionCircleStates.INSIDE_FENCE;
            } else {
                state = PositionCircleStates.ACTIVITY_STARTED;
            }
            const positionCircle = createPositionCircle(currentPosition.value[0], currentPosition.value[1], activityStore.currentRadius);
            positionCircle.properties = { state };
            return featureCollection([positionCircle]);
        }
        return featureCollection([]);
    });

    const isInsideFence = computed(() => {
        return Object.values(insideFenceDict.value).some((value) => value);
    });

    const isPositionInaccurate = computed(() => {
        return inaccuracyCounter.value > 100;
    });

    const checkIfPositionIsInsideAFence = (longitude: number, latitude: number) => {
        const isInside = [];
        const positionCircle = createPositionCircle(longitude, latitude, activityStore.currentRadius);
        for (const feature of fenceStore.fenceFeatureCollection.features) {
            if (booleanIntersects(positionCircle, feature.geometry as PolygonTypes)) {
                isInside.push(feature);
            }
        }
        return isInside;
    };

    const resetInsideFenceDict = (except = {}) => {
        for (const key in insideFenceDict.value) {
            if (except[key]) {
                continue;
            }
            insideFenceDict.value[key] = false;
        }
    };

    const scheduleNotification = (title: string, body: string) => {
        LocalNotifications.schedule({
            notifications: [{
                title,
                body,
                smallIcon: "alert_circle_outline",
                largeIcon: "alert_circle_outline",
                iconColor: "red",
                id: notificationIdentifier.value++,
                schedule: { at: new Date(Date.now() + waitTimeNotification), allowWhileIdle: true },
            }]
        });
    };

    const calculateAccuracy = (accuracy: number) => {
        const isAccurate = accuracy / activityStore.currentRadius < 0.5;
        if (!isAccurate) {
            inaccuracyCounter.value++;
            if (inaccuracyCounter.value === 100) {
                // TODO: this method is a bit to insensitive for short periods of accuracy during long periods of inaccuracy
                scheduleNotification(t("notification.inaccuratePositionTitle"), t("notification.inaccuratePositionBody"));
            }
        } else {
            inaccuracyCounter.value = 0;
        }
    };

    // TOOD: this function needs to be as performant as possible
    const notifyUserIfInsideFence = async (longitude: number, latitude: number) => {
        const foundFeatures = checkIfPositionIsInsideAFence(longitude, latitude);
        if (foundFeatures && foundFeatures.length > 0) {

            const names = [];
            const except = {}; // helper to reset only the ones that are not inside anymore

            for (const feature of foundFeatures) {
                const id = feature.properties.documentId;
                if (insideFenceDict.value[id]) {
                    except[id] = true;
                    continue; // already notified
                }
                names.push(feature.properties.name);
                insideFenceDict.value[id] = true;
                except[id] = true;
            }
            resetInsideFenceDict(except);

            if (names.length > 0) {
                const body = joinArraysWithConjunction(names, locale.value);
                scheduleNotification(title, body);
            }
        } else if (Object.keys(insideFenceDict).length > 0) {
            resetInsideFenceDict();
        }
    };

    const startWatch = async () => {
        Geolocation.getCurrentPosition()
            .then((pos) => {
                currentPosition.value = [pos.coords.longitude, pos.coords.latitude, pos.coords.accuracy];
                notifyUserIfInsideFence(pos.coords.longitude, pos.coords.latitude);
            })
            .catch((error: GeolocationPositionError) => {
                const msg = "Error while retrieving current position on activity start";
                Monitoring.chainError(msg, new Error(error.message));
            });

        BackgroundGeolocation.addWatcher({
            backgroundMessage: t("permissions.backgroundLocationIsOnMessage"),
            backgroundTitle: t("permissions.backgroundLocationIsOnTitle"),
            distanceFilter: 5,
            requestPermissions: true,
            stale: false
        }, async (location, error) => {
            if (error) {
                if (error.code === "NOT_AUTHORIZED") {
                    const errorAlert = await alertStore.getMissingPermissionAlert(() => BackgroundGeolocation.openSettings());
                    errorAlert.present();
                } else {
                    Monitoring.chainError("Error while tracking background-geolocation", new Error(error.code));
                }
            }

            if (location) {
                currentPosition.value = [location.longitude, location.latitude, location.accuracy];
                notifyUserIfInsideFence(location.longitude, location.latitude);
                calculateAccuracy(location.accuracy);
            }
        })
            .then((watchId) => {
                currentWatchId.value = watchId;
            })
            .catch((error) => Monitoring.chainError("Error while starting background-geolocation tracking", error));
    };

    const stopWatch = () => {
        if (currentWatchId.value) {
            BackgroundGeolocation.removeWatcher({ id: currentWatchId.value });
            currentWatchId.value = null;
        }
    };

    const requestNotificationPermissions = () => {
        return LocalNotifications.requestPermissions();
    };

    watch(() => activityStore.currentRadius, () => {
        if (activityStore.isActivityRunning && currentPosition.value) {
            notifyUserIfInsideFence(currentPosition.value[0], currentPosition.value[1]);
        }
    });

    return {
        startWatch,
        stopWatch,
        requestNotificationPermissions,
        isInsideFence,
        isPositionInaccurate,
        currentPosition,
        currentPositionCircleFeatureCollection
    };
});